import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Button,
    Input,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    InputGroup,
    InputGroupAddon,
    InputGroupText
} from "reactstrap";

import classnames from "classnames";
import CommonHeader from "components/Headers/CommonHeader";
import Swal from "sweetalert2";

import {
    getReferralConfig,
    updateReferralConfig,
    createReferralConfig
} from "../../api/ApiServices";

const ReferralRewardManagement = () => {

    const [activeTab, setActiveTab] = useState("1");
    const [loading, setLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [errors, setErrors] = useState({
        monetary: {},
        points: {}
    });

    const [form, setForm] = useState({
        monetary: {
            status: true,
            same_country_reward: "",
            cross_country_reward: "",
            max_reward_per_user: "",
            max_system_limit: ""
        },

        points: {
            status: true,
            same_country_points: "",
            cross_country_points: "",
            max_points_per_user: "",
            max_total_points: ""
        }
    });

    useEffect(() => {
        loadConfig();
    }, []);

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    // ================= LOAD CONFIG =================
    const loadConfig = async () => {
        try {

            const res = await getReferralConfig();
            const data = res?.data;

            if (data) {

                setIsEdit(true);

                setForm({
                    monetary: {
                        status: data?.rc_status === "1",

                        same_country_reward:
                            data?.rc_same_country_reward ?? "",

                        cross_country_reward:
                            data?.rc_cross_country_reward ?? "",

                        max_reward_per_user:
                            data?.rc_max_reward_per_user ?? "",

                        max_system_limit:
                            data?.rc_max_system_limit ?? ""
                    },

                    points: {
                        status: data?.points_status === "1",

                        same_country_points:
                            data?.rc_same_country_points ?? "",

                        cross_country_points:
                            data?.rc_cross_country_points ?? "",

                        max_points_per_user:
                            data?.rc_max_points_per_user ?? "",

                        max_total_points:
                            data?.rc_max_total_points ?? ""
                    }
                });

            } else {
                setIsEdit(false);
            }

        } catch (err) {
            console.log(err);
            setIsEdit(false);
        }
    };

    // ================= INPUT CHANGE =================
    const handleChange = (section, field, value) => {
        const numericVal = parseFloat(value);
        let errorMsg = null;
        if (!isNaN(numericVal) && numericVal < 0) {
            errorMsg = "Negative values are not allowed";
        }

        setErrors((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: errorMsg
            }
        }));

        setForm((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    // ================= TOGGLE =================
    const handleToggle = (section) => {
        setForm((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                status: !prev[section].status
            }
        }));
    };

    // ================= SAVE =================
    const handleSave = async () => {
        let hasErrors = false;
        const newErrors = { monetary: {}, points: {} };

        if (form.monetary.status) {
            const keys = ["same_country_reward", "cross_country_reward", "max_reward_per_user", "max_system_limit"];
            keys.forEach(key => {
                const val = parseFloat(form.monetary[key]);
                if (!isNaN(val) && val < 0) {
                    newErrors.monetary[key] = "Negative values are not allowed";
                    hasErrors = true;
                }
            });
        }

        if (form.points.status) {
            const keys = ["same_country_points", "cross_country_points", "max_points_per_user", "max_total_points"];
            keys.forEach(key => {
                const val = parseFloat(form.points[key]);
                if (!isNaN(val) && val < 0) {
                    newErrors.points[key] = "Negative values are not allowed";
                    hasErrors = true;
                }
            });
        }

        if (hasErrors) {
            setErrors(newErrors);
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: "Please correct the negative values before saving."
            });
            return;
        }

        try {

            setLoading(true);

            const payload = {

                rc_status:
                    form.monetary.status ? "1" : "0",

                points_status:
                    form.points.status ? "1" : "0",

                rc_same_country_reward:
                    form.monetary.same_country_reward,

                rc_cross_country_reward:
                    form.monetary.cross_country_reward,

                rc_max_reward_per_user:
                    form.monetary.max_reward_per_user,

                rc_max_system_limit:
                    form.monetary.max_system_limit,

                rc_same_country_points:
                    form.points.same_country_points,

                rc_cross_country_points:
                    form.points.cross_country_points,

                rc_max_points_per_user:
                    form.points.max_points_per_user,

                rc_max_total_points:
                    form.points.max_total_points
            };

            if (isEdit) {
                await updateReferralConfig(payload);
            } else {
                await createReferralConfig(payload);
            }

            Swal.fire({
                icon: "success",
                title: isEdit
                    ? "Updated Successfully"
                    : "Created Successfully",

                text: "Referral configuration saved.",
                timer: 1200,
                showConfirmButton: false
            });

            setIsEdit(true);

        } catch (err) {

            console.log(err);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: err?.message || "Something went wrong"
            });

        } finally {
            setLoading(false);
        }
    };

    // ================= STYLES =================
    const styles = {

        sectionCard: {
            borderRadius: "14px",
            border: "1px solid rgba(0,0,0,0.04)",
            boxShadow: "0 6px 18px rgba(0,0,0,0.05)"
        },

        saveBtn: {
            borderRadius: "30px",
            padding: "10px 28px",
            fontWeight: "600"
        }
    };

    return (
        <>
            <CommonHeader />

            {/* ================= TOGGLE CSS ================= */}
            <style>
                {`
                    .switch {
                        position: relative;
                        display: inline-block;
                        width: 46px;
                        height: 24px;
                    }

                    .switch input {
                        opacity: 0;
                        width: 0;
                        height: 0;
                    }

                    .slider {
                        position: absolute;
                        cursor: pointer;
                        inset: 0;
                        background-color: #d1d5db;
                        transition: .3s;
                        border-radius: 34px;
                    }

                    .slider:before {
                        position: absolute;
                        content: "";
                        height: 18px;
                        width: 18px;
                        left: 3px;
                        bottom: 3px;
                        background-color: white;
                        transition: .3s;
                        border-radius: 50%;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                    }

                    .switch input:checked + .slider {
                        background-color: #5e72e4;
                    }

                    .switch input:checked + .slider:before {
                        transform: translateX(22px);
                    }

                    .readonly-input {
                        background-color: #fff !important;
                        opacity: 1 !important;
                        cursor: not-allowed;
                    }
                `}
            </style>

            <Container className="mt--7 pb-5" fluid>

                {/* ================= HEADER ================= */}
                <Card className="mb-4 shadow-sm" style={{ borderRadius: "18px" }}>
                    <CardBody className="py-4">
                        <Row className="align-items-center">
                            <Col md="8">
                                <h3 className="mb-1 font-weight-bold text-dark">
                                    Referral Reward Management
                                </h3>

                                <p className="text-muted mb-0">
                                    Configure monetary rewards and point based incentives
                                </p>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* ================= TABS ================= */}
                <Nav pills className="mb-4 ml-3">

                    <NavItem>
                        <NavLink
                            className={classnames("px-4 py-2", {
                                active: activeTab === "1"
                            })}
                            onClick={() => toggleTab("1")}
                            style={{ borderRadius: "25px" }}
                        >
                            Monetary Rewards Settings
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            className={classnames("px-4 py-2", {
                                active: activeTab === "2"
                            })}
                            onClick={() => toggleTab("2")}
                            style={{ borderRadius: "25px" }}
                        >
                            Point System Settings
                        </NavLink>
                    </NavItem>

                </Nav>

                <TabContent activeTab={activeTab}>

                    {/* ================= MONETARY TAB ================= */}
                    <TabPane tabId="1">

                        <Card style={styles.sectionCard} className="mb-4">

                            <CardHeader className="bg-white border-0">
                                <div className="d-flex justify-content-between align-items-center">

                                    <h5 className="mb-0 font-weight-semibold">
                                        Reward Configuration
                                    </h5>

                                    <div className="d-flex align-items-center">

                                        <span className="mr-2 text-muted small">
                                            {form.monetary.status
                                                ? "Enabled"
                                                : "Disabled"}
                                        </span>

                                        <label className="switch mb-0">
                                            <input
                                                type="checkbox"
                                                checked={form.monetary.status}
                                                onChange={() =>
                                                    handleToggle("monetary")
                                                }
                                            />

                                            <span className="slider"></span>
                                        </label>
                                    </div>
                                </div>
                            </CardHeader>

                            <CardBody>
                                <Row>

                                    <Col md="6">
                                        <div className="form-group">
                                            <label className="form-control-label text-muted mb-2">
                                                Same Country Reward
                                            </label>

                                            <InputGroup>
                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText>$</InputGroupText>
                                                </InputGroupAddon>

                                                <Input
                                                    type="number"
                                                    min="0"
                                                    readOnly={!form.monetary.status}
                                                    className={!form.monetary.status ? "readonly-input" : ""}
                                                    value={form.monetary.same_country_reward}
                                                    invalid={!!errors.monetary.same_country_reward}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            "monetary",
                                                            "same_country_reward",
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Enter reward amount"
                                                />
                                            </InputGroup>
                                            {errors.monetary.same_country_reward && <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>{errors.monetary.same_country_reward}</div>}
                                        </div>
                                    </Col>

                                    <Col md="6">
                                        <div className="form-group">

                                            <label className="form-control-label text-muted mb-2">
                                                Cross Country Reward
                                            </label>

                                            <InputGroup>

                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText>$</InputGroupText>
                                                </InputGroupAddon>

                                                <Input
                                                    type="number"
                                                    min="0"
                                                    readOnly={!form.monetary.status}
                                                    className={!form.monetary.status ? "readonly-input" : ""}
                                                    value={form.monetary.cross_country_reward}
                                                    invalid={!!errors.monetary.cross_country_reward}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            "monetary",
                                                            "cross_country_reward",
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Enter reward amount"
                                                />

                                            </InputGroup>
                                            {errors.monetary.cross_country_reward && <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>{errors.monetary.cross_country_reward}</div>}
                                        </div>
                                    </Col>

                                </Row>
                            </CardBody>
                        </Card>

                        <Card style={styles.sectionCard}>
                            <CardHeader className="bg-white border-0">
                                <h5 className="mb-0 font-weight-semibold">
                                    Governance & Financial Limits
                                </h5>
                            </CardHeader>

                            <CardBody>
                                <Row>

                                    <Col md="6">
                                        <div className="form-group">
                                            <label className="form-control-label text-muted mb-2">
                                                Max Reward Per User
                                            </label>

                                            <Input
                                                type="number"
                                                min="0"
                                                readOnly={!form.monetary.status}
                                                className={!form.monetary.status ? "readonly-input" : ""}
                                                value={form.monetary.max_reward_per_user}
                                                invalid={!!errors.monetary.max_reward_per_user}
                                                onChange={(e) =>
                                                    handleChange(
                                                        "monetary",
                                                        "max_reward_per_user",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            {errors.monetary.max_reward_per_user && <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>{errors.monetary.max_reward_per_user}</div>}
                                        </div>
                                    </Col>

                                    <Col md="6">
                                        <div className="form-group">
                                            <label className="form-control-label text-muted mb-2">
                                                Max System Budget
                                            </label>

                                            <Input
                                                type="number"
                                                min="0"
                                                readOnly={!form.monetary.status}
                                                className={!form.monetary.status ? "readonly-input" : ""}
                                                value={form.monetary.max_system_limit}
                                                invalid={!!errors.monetary.max_system_limit}
                                                onChange={(e) =>
                                                    handleChange(
                                                        "monetary",
                                                        "max_system_limit",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            {errors.monetary.max_system_limit && <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>{errors.monetary.max_system_limit}</div>}
                                        </div>
                                    </Col>

                                </Row>
                            </CardBody>
                        </Card>
                    </TabPane>

                    {/* ================= POINT TAB ================= */}
                    <TabPane tabId="2">

                        <Card style={styles.sectionCard} className="mb-4">

                            <CardHeader className="bg-white border-0">
                                <div className="d-flex justify-content-between align-items-center">

                                    <h5 className="mb-0 font-weight-semibold">
                                        Point Allocation Rules
                                    </h5>

                                    <div className="d-flex align-items-center">

                                        <span className="mr-2 text-muted small">
                                            {form.points.status
                                                ? "Enabled"
                                                : "Disabled"}
                                        </span>

                                        <label className="switch mb-0">
                                            <input
                                                type="checkbox"
                                                checked={form.points.status}
                                                onChange={() =>
                                                    handleToggle("points")
                                                }
                                            />

                                            <span className="slider"></span>
                                        </label>
                                    </div>
                                </div>
                            </CardHeader>

                            <CardBody>
                                <Row>

                                    <Col md="6">
                                        <div className="form-group">
                                            <label className="form-control-label text-muted mb-2">
                                                Same Country Points
                                            </label>

                                            <InputGroup>

                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText>Points</InputGroupText>
                                                </InputGroupAddon>

                                                <Input
                                                    type="number"
                                                    min="0"
                                                    readOnly={!form.points.status}
                                                    className={!form.points.status ? "readonly-input" : ""}
                                                    value={form.points.same_country_points}
                                                    invalid={!!errors.points.same_country_points}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            "points",
                                                            "same_country_points",
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Enter points"
                                                />
                                            </InputGroup>
                                            {errors.points.same_country_points && <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>{errors.points.same_country_points}</div>}
                                        </div>
                                    </Col>

                                    <Col md="6">
                                        <div className="form-group">
                                            <label className="form-control-label text-muted mb-2">
                                                Cross Country Points
                                            </label>

                                            <InputGroup>

                                                <InputGroupAddon addonType="prepend">
                                                    <InputGroupText>Points</InputGroupText>
                                                </InputGroupAddon>

                                                <Input
                                                    type="number"
                                                    min="0"
                                                    readOnly={!form.points.status}
                                                    className={!form.points.status ? "readonly-input" : ""}
                                                    value={form.points.cross_country_points}
                                                    invalid={!!errors.points.cross_country_points}
                                                    onChange={(e) =>
                                                        handleChange(
                                                            "points",
                                                            "cross_country_points",
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Enter points"
                                                />
                                            </InputGroup>
                                            {errors.points.cross_country_points && <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>{errors.points.cross_country_points}</div>}
                                        </div>
                                    </Col>

                                </Row>
                            </CardBody>
                        </Card>

                        <Card style={styles.sectionCard}>
                            <CardHeader className="bg-white border-0">
                                <h5 className="mb-0 font-weight-semibold">
                                    Governance & Thresholds
                                </h5>
                            </CardHeader>

                            <CardBody>
                                <Row>

                                    <Col md="6">
                                        <div className="form-group">
                                            <label className="form-control-label text-muted mb-2">
                                                Max Points Per User
                                            </label>

                                            <Input
                                                type="number"
                                                min="0"
                                                readOnly={!form.points.status}
                                                className={!form.points.status ? "readonly-input" : ""}
                                                value={form.points.max_points_per_user}
                                                invalid={!!errors.points.max_points_per_user}
                                                onChange={(e) =>
                                                    handleChange(
                                                        "points",
                                                        "max_points_per_user",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            {errors.points.max_points_per_user && <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>{errors.points.max_points_per_user}</div>}
                                        </div>
                                    </Col>

                                    <Col md="6">
                                        <div className="form-group">
                                            <label className="form-control-label text-muted mb-2">
                                                Total System Point Cap
                                            </label>

                                            <Input
                                                type="number"
                                                min="0"
                                                readOnly={!form.points.status}
                                                className={!form.points.status ? "readonly-input" : ""}
                                                value={form.points.max_total_points}
                                                invalid={!!errors.points.max_total_points}
                                                onChange={(e) =>
                                                    handleChange(
                                                        "points",
                                                        "max_total_points",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            {errors.points.max_total_points && <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>{errors.points.max_total_points}</div>}
                                        </div>
                                    </Col>

                                </Row>
                            </CardBody>
                        </Card>

                    </TabPane>

                </TabContent>

                {/* ================= SAVE BUTTON ================= */}
                <div className="text-right mt-4">

                    <Button
                        color="primary"
                        onClick={handleSave}
                        disabled={loading}
                        style={styles.saveBtn}
                    >
                        {loading ? "Saving..." : "Save Changes"}
                    </Button>

                </div>

            </Container>
        </>
    );
};

export default ReferralRewardManagement;